// Which camera can this phone run? Decided once, before the camera opens,
// and only ever stepped DOWN at run time (camera blocked, model too slow).
//
//   smart   live camera + on-device car detection (brackets lock, auto-snap)
//   guided  live camera, fixed brackets, light/blur hints, manual shutter
//   native  the phone's own camera app, one photo at a time, with coach cards
//
// In-app browsers (Facebook, Instagram, TikTok, Snapchat…) often block or
// break the live camera, so they go straight to native with a hint to open
// the link in the real browser. Pure; unit-tested.

export type CaptureMode = "smart" | "guided" | "native"

export interface CaptureEnvironment {
  userAgent: string
  isSecureContext: boolean
  hasGetUserMedia: boolean
  // navigator.connection (Chrome / Android)
  saveData?: boolean
  effectiveType?: string
  // navigator.deviceMemory in GB (Chrome / Android)
  deviceMemory?: number
}

export interface CaptureCapability {
  mode: CaptureMode
  inAppBrowser: boolean
  // Why we stepped down (for the debug overlay and the design log).
  reason: "in-app-browser" | "insecure" | "no-camera-api" | "low-end" | null
}

const IN_APP = /FBAN|FBAV|FB_IAB|FBIOS|Instagram|Line\/|musical_ly|BytedanceWebview|TikTok|Snapchat|Twitter|Pinterest|LinkedInApp/i

export function isInAppBrowser(userAgent: string): boolean {
  return IN_APP.test(userAgent)
}

export function decideCapability(env: CaptureEnvironment): CaptureCapability {
  const inAppBrowser = isInAppBrowser(env.userAgent)
  if (inAppBrowser) return { mode: "native", inAppBrowser, reason: "in-app-browser" }
  if (!env.isSecureContext) return { mode: "native", inAppBrowser, reason: "insecure" }
  if (!env.hasGetUserMedia) return { mode: "native", inAppBrowser, reason: "no-camera-api" }
  const lowEnd =
    env.saveData === true ||
    env.effectiveType === "slow-2g" ||
    env.effectiveType === "2g" ||
    (typeof env.deviceMemory === "number" && env.deviceMemory < 2)
  if (lowEnd) return { mode: "guided", inAppBrowser, reason: "low-end" }
  return { mode: "smart", inAppBrowser, reason: null }
}

interface NavigatorExtras {
  connection?: { saveData?: boolean; effectiveType?: string }
  deviceMemory?: number
}

export function readEnvironment(): CaptureEnvironment {
  const nav = navigator as Navigator & NavigatorExtras
  return {
    userAgent: nav.userAgent,
    isSecureContext: window.isSecureContext,
    hasGetUserMedia: typeof nav.mediaDevices?.getUserMedia === "function",
    saveData: nav.connection?.saveData,
    effectiveType: nav.connection?.effectiveType,
    deviceMemory: nav.deviceMemory,
  }
}
